import { useEffect, useState } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import MergeWhite from "../assets/images/merge-white.png";
import {
  getAllAcumaticaProjects,
  getCostCodesByProjectId,
  getProjectCheckList,
  getProjectTask,
  getProjectTaskAndCostCode,
  mergeProjectAndTasks,
} from "../API";

// project_check_list.status enum, mirrored from the backend's StatusEnum.
const CHECKLIST_STATUS = {
  0: "New",
  1: "In progress",
  2: "Submitted",
  3: "In review",
  4: "Approved",
};

// react-select menus are portalled to <body> so they can escape the modal's scroll container.
const portalStyles = { menuPortal: (base) => ({ ...base, zIndex: 9999 }) };

const MergeProjectModal = (props) => {
  const { projectId, sourceName } = props;
  const [tempProjectTasks, setTempProjectTasks] = useState([]);
  const [getProjectList, setAllProjectOptions] = useState([]);
  const [costCodeList, setCostCodeList] = useState();
  const [tasksList, setTasksList] = useState([]);
  const [selectedProject, setSelectedProject] = useState({});
  const [selectedProjectTasks, setSelectedProjectTasks] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedAcumaticaTask, setSelectedAcumaticaTask] = useState([]);
  const [selectedAcumaticaCostCode, setSelectedAcumaticaCostCode] = useState(
    []
  );
  // OpsEdge checklists on the temporary project; they move with the merge, so the admin
  // should see exactly which ones before confirming.
  const [sourceCheckLists, setSourceCheckLists] = useState([]);

  const resetFields = () => {
    setSelectedAcumaticaTask([]);
    setSelectedAcumaticaCostCode([]);
    setSelectedProject({});
    setSelectedProjectTasks([]);
  };

  const navigate = useNavigate();

  useEffect(() => {
    getAllTempProjects();
    getAllAcumaticaTasks();
    getSourceCheckLists();
  }, []);

  const getSourceCheckLists = async () => {
    try {
      const res = await getProjectCheckList(`project_id=${projectId}`);
      if (res?.status === 200 && Array.isArray(res?.data)) {
        setSourceCheckLists(res.data);
      }
    } catch (error) {
      // The list is informational; a failed lookup must not block the merge.
      setSourceCheckLists([]);
    }
  };

  const getAllTempProjects = async () => {
    await getAllAcumaticaProjects().then((res) => {
      if (res.status === 200) {
        const tempProjectDetails = res?.data?.map((project) => {
          return {
            label: `(${project?.acumatica_project_id}) ${project?.description}`,
            value: project?.id,
          };
        });
        setAllProjectOptions(tempProjectDetails);
      }
    });
  };

  // A task whose entries carry no cost code -- every task used only by equipment -- comes
  // back with cost_code_id null and needs a task mapping only. When a task has BOTH kinds of
  // entry the API returns a null-cost-code row alongside the real pairs; that row is redundant
  // (the task is already mappable through the real pair) so it is dropped here.
  const dropRedundantTaskOnlyRows = (rows = []) => {
    const tasksWithCostCode = new Set(
      rows.filter((row) => row?.cost_code_id).map((row) => row?.task_id)
    );
    return rows.filter(
      (row) => row?.cost_code_id || !tasksWithCostCode.has(row?.task_id)
    );
  };

  const getAllAcumaticaTasks = async () => {
    const query = `project_id=${projectId}`;
    await getProjectTaskAndCostCode(query).then((res) => {
      if (res.status === 200) {
        const projectTasks = dropRedundantTaskOnlyRows(res?.data)?.map(
          (task) => {
            return { label: task?.task, value: task };
          }
        );
        setTempProjectTasks(projectTasks);
      }
    });
  };

  // True for rows that need only a task mapping, so the cost-code column is inert for them.
  const isTaskOnlyRow = (task) => !task?.value?.cost_code_id;

  // A merge rejection is a deliberate 422 that names exactly what needs fixing, and a mapping
  // failure carries a details[] listing every problem at once. Surfacing only "Something went
  // wrong" would waste all of it and leave the admin with no way to act.
  const describeMergeFailure = (error) => {
    const body = error?.response?.data;
    if (!body?.message) {
      return "Something went wrong";
    }
    const problems = (body.details || [])
      .map((detail) => detail?.message)
      .filter(Boolean);
    return problems.length
      ? `${body.message} ${problems.join(" ")}`
      : body.message;
  };

  const fetchCostCodes = async (data, index) => {
    const payload = {
      id: selectedProject?.value,
      is_default: true,
      query: `project_task_id=${data?.value?.id}`,
    };
    await getCostCodesByProjectId(
      payload?.id,
      payload?.is_default,
      payload?.query
    ).then((res) => {
      const projectCostCodeOptions = res?.data?.map((costCode) => ({
        label: `${costCode?.description} (${costCode?.cost_code})`,
        value: costCode,
        index: index,
      }));

      setCostCodeList((prevState) => {
        let newCostCodeList = Array.isArray(prevState) ? [...prevState] : [];
        newCostCodeList = newCostCodeList.filter(
          (costCode) => costCode.index !== index
        );
        if (projectCostCodeOptions.length > 0) {
          newCostCodeList.push(...projectCostCodeOptions);
        }
        return newCostCodeList;
      });
    });
  };

  const handleAcumaticaTaskChange = (data, tempTask, index) => {
    setSelectedAcumaticaCostCode((prevState) =>
      prevState.filter((ele) => ele.index !== index)
    );
    setSelectedAcumaticaTask((prevState) => {
      const existingIndex = prevState.findIndex((task) => task.index === index);
      if (existingIndex !== -1) {
        const updatedState = [...prevState];
        updatedState[existingIndex] = { ...data, index };
        return updatedState;
      }
      return [...prevState, { ...data, index }];
    });
    // Drop this row's options synchronously. fetchCostCodes only prunes them inside its .then,
    // so until it resolves — or if it never returns data — the PREVIOUS destination task's cost
    // codes stay selectable, and picking one submits a cost code that hangs off a different
    // task. The backend gate rejects that, but the merge fails for a reason the user caused by
    // being quick rather than wrong.
    setCostCodeList((prevState) =>
      Array.isArray(prevState)
        ? prevState.filter((costCode) => costCode.index !== index)
        : prevState
    );
    // A task-only row has no cost code to choose, so there is nothing to fetch.
    if (!isTaskOnlyRow(tempTask)) {
      fetchCostCodes(data, index);
    }
    const source_task = tempTask?.value?.task_id;
    const destination_task = data?.value?.id;

    setSelectedProjectTasks((prevState) => {
      const prevIndex = prevState?.findIndex((ele) => ele?.index === index);
      if (prevIndex >= 0) {
        prevState[prevIndex].source_task = source_task;
        prevState[prevIndex].destination_task = destination_task;
        return [...prevState];
      } else {
        return [...prevState, { source_task, destination_task, index }];
      }
    });
  };

  const handleAcumaticaCostCodeChange = (data, tempCostCode, index) => {
    setSelectedAcumaticaCostCode((prevState) => {
      const existingIndex = prevState.findIndex(
        (costCode) => costCode.index === index
      );
      if (existingIndex !== -1) {
        const updatedState = [...prevState];
        updatedState[existingIndex] = { ...data, index, tempCostCode };
        return updatedState;
      }
      return [...prevState, { ...data, index, tempCostCode }];
    });

    const source_cost_code = tempCostCode?.value?.cost_code_id;
    const destination_cost_code = data?.value?.id;

    setSelectedProjectTasks((prevState) => {
      const prevIndex = prevState?.findIndex((ele) => ele?.index === index);
      if (prevIndex >= 0) {
        prevState[prevIndex].source_cost_code = source_cost_code;
        prevState[prevIndex].destination_cost_code = destination_cost_code;
        return [...prevState];
      } else {
        return [
          ...prevState,
          { source_cost_code, destination_cost_code, index },
        ];
      }
    });
  };

  const handleProjectChange = async (data) => {
    setLoading(true);
    setSelectedProject(data);
    setSelectedProjectTasks([]);
    setCostCodeList([]);
    setSelectedAcumaticaTask([]);
    setSelectedAcumaticaCostCode([]);
    await getProjectTask(data?.value)
      .then((res) => {
        setLoading(false);
        if (res?.status === 200) {
          const projectTasks = res?.data?.map((task) => {
            return { label: task?.description, value: task };
          });
          setTasksList(projectTasks);
          setSelectedProjectTasks([]);
        }
      })
      .catch((err) => setLoading(false));
  };

  const onClickMerge = async () => {
    // Only rows that actually have a cost code need one selected. Counting every row here is
    // what made a project containing equipment-only tasks impossible to submit.
    const rowsNeedingCostCode = tempProjectTasks.filter(
      (task) => !isTaskOnlyRow(task)
    ).length;

    if (
      projectId &&
      selectedProject?.value &&
      selectedProjectTasks?.length === tempProjectTasks?.length &&
      selectedAcumaticaCostCode?.length === rowsNeedingCostCode
    ) {
      const allSelectedProjectTasks = selectedProjectTasks.map(
        ({ index, ...rest }) => {
          // Send a task-only mapping rather than null cost-code fields: the API accepts the
          // pair as optional, but rejects a null that is present.
          if (!rest.source_cost_code || !rest.destination_cost_code) {
            const { source_cost_code, destination_cost_code, ...taskOnly } =
              rest;
            return taskOnly;
          }
          return rest;
        }
      );
      const payload = {
        project: {
          source: parseInt(projectId),
          destination: selectedProject.value,
        },
        project_task_code_code: allSelectedProjectTasks,
      };
      await mergeProjectAndTasks(payload)
        .then((res) => {
          if (res?.status === 200) {
            toast.success("Project merged successfully");
            resetFields();
            navigate("/projects");
          }
        })
        .catch((error) => {
          toast.error(describeMergeFailure(error));
        });
    } else {
      toast.warning("Map every task and cost code before merging.");
    }
  };

  const getCostCodes = (index) => {
    return costCodeList?.filter((ele) => ele?.index === index);
  };

  const getSelectedTask = (index) => {
    const findSelectedTask = selectedAcumaticaTask?.find(
      (ele) => ele?.index === index
    );
    return findSelectedTask || null;
  };

  const getSelectedCostCode = (index) => {
    const findSelectedCostCode = selectedAcumaticaCostCode?.find(
      (ele) => ele?.index === index
    );
    return findSelectedCostCode || null;
  };

  // Presentation-only derived state. The submit guard in onClickMerge stays the source of
  // truth; this mirrors the same rule so the counter and the button never disagree with it.
  const destinationChosen = Boolean(selectedProject?.value);
  const totalRows = tempProjectTasks?.length || 0;
  const isRowMapped = (task, index) =>
    Boolean(getSelectedTask(index)) &&
    (isTaskOnlyRow(task) || Boolean(getSelectedCostCode(index)));
  const mappedRows = tempProjectTasks.filter((task, index) =>
    isRowMapped(task, index)
  ).length;
  // Zero rows is a valid merge: a temporary project used only for OpsEdge checklists has no
  // time entries to map, and its checklists still move (FIX-merge-checklist-only-project).
  const isReady = destinationChosen && mappedRows === totalRows;
  const sourceLabel = sourceName || "this temporary project";
  const checkListCount = sourceCheckLists.length;
  const checkListNoun = checkListCount === 1 ? "checklist" : "checklists";
  const progressText = !destinationChosen
    ? "Choose an Acumatica project to start"
    : totalRows === 0
    ? checkListCount > 0
      ? `Nothing to map — ready to merge ${checkListCount} ${checkListNoun}`
      : "Nothing to map and no checklists — nothing will move"
    : `${mappedRows} of ${totalRows} rows mapped`;

  const taskPlaceholder = destinationChosen
    ? "Select task…"
    : "Choose an Acumatica project first";
  const costCodePlaceholder = (task, index) => {
    if (isTaskOnlyRow(task)) return "Not required";
    return getSelectedTask(index) ? "Select cost code…" : "Choose a task first";
  };

  return (
    <>
      <div
        className="modal fade merge-project-modal"
        id="mergeProjectModal"
        tabIndex="-1"
        aria-labelledby="mergeProjectModalLabel"
        aria-hidden="true"
        data-bs-backdrop="static"
      >
        <div className="modal-dialog modal-xl modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header merge-project-modal__header">
              <div>
                <h5
                  className="modal-title font-weight-bold"
                  id="mergeProjectModalLabel"
                >
                  Merge temporary project
                </h5>
                <p className="merge-project-modal__subtitle">
                  Merging <strong>{sourceLabel}</strong> into an Acumatica
                  project
                </p>
              </div>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={() => resetFields()}
              ></button>
            </div>

            <div className="modal-body merge-project-modal__body">
              <div className="merge-project-modal__intro" role="note">
                <p className="merge-project-modal__intro-title">
                  What happens when you merge
                </p>
                <ul>
                  <li>
                    All time entries, weekly summaries and OpsEdge checklists
                    move to the Acumatica project you choose.
                  </li>
                  <li>The temporary project is removed afterwards.</li>
                  <li>
                    <strong>This cannot be undone.</strong> Check the mapping
                    before you merge.
                  </li>
                </ul>
              </div>

              <section className="merge-project-modal__step">
                <label
                  htmlFor="merge-destination-project"
                  className="merge-project-modal__step-title"
                >
                  1. Acumatica project to merge into
                  <span className="merge-project-modal__required" aria-hidden="true">
                    {" "}
                    *
                  </span>
                </label>
                <Select
                  inputId="merge-destination-project"
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  styles={portalStyles}
                  isLoading={loading}
                  isSearchable
                  placeholder="Search by project number or name…"
                  noOptionsMessage={() => "No Acumatica projects found"}
                  value={destinationChosen ? selectedProject : null}
                  onChange={(data) => handleProjectChange(data)}
                  aria-label="Acumatica project"
                  options={getProjectList}
                />
              </section>

              <section className="merge-project-modal__step">
                <h6 className="merge-project-modal__step-title">
                  2. Map each task and cost code
                </h6>
                <p className="merge-project-modal__hint">
                  Every row must be mapped. Cost codes are only required where
                  the temporary task has one.
                </p>
                <div className="merge-mapping-table-wrap">
                  <table className="merge-mapping-table">
                    <thead>
                      <tr className="merge-mapping-table__group-row">
                        <th colSpan={2} scope="colgroup">
                          From: temporary project
                        </th>
                        <th colSpan={2} scope="colgroup">
                          To: Acumatica project
                        </th>
                      </tr>
                      <tr>
                        <th scope="col">Task</th>
                        <th scope="col">Cost code</th>
                        <th scope="col">Task</th>
                        <th scope="col">Cost code</th>
                      </tr>
                    </thead>
                    <tbody>
                      {totalRows > 0 ? (
                        tempProjectTasks.map((task, index) => (
                          // Rows are one per (task, cost code) pair, so task_id alone is not
                          // unique. Every piece of row state is index-keyed, so the index IS
                          // the row identity here.
                          <tr
                            key={index}
                            className={
                              isRowMapped(task, index)
                                ? "merge-mapping-table__row is-mapped"
                                : "merge-mapping-table__row"
                            }
                          >
                            <td className="merge-mapping-table__source">
                              {task?.label}
                            </td>
                            <td className="merge-mapping-table__source">
                              {isTaskOnlyRow(task) ? (
                                <span className="text-muted">
                                  No cost code (equipment)
                                </span>
                              ) : (
                                task?.value?.cost_code
                              )}
                            </td>
                            <td>
                              <Select
                                classNamePrefix="react-select"
                                menuPortalTarget={document.body}
                                styles={portalStyles}
                                value={getSelectedTask(index)}
                                isDisabled={!destinationChosen}
                                isLoading={loading}
                                placeholder={taskPlaceholder}
                                onChange={(data) =>
                                  handleAcumaticaTaskChange(data, task, index)
                                }
                                aria-label={`Acumatica task for ${task?.label}`}
                                options={tasksList}
                              />
                            </td>
                            <td>
                              <Select
                                classNamePrefix="react-select"
                                menuPortalTarget={document.body}
                                styles={portalStyles}
                                value={getSelectedCostCode(index)}
                                isDisabled={
                                  isTaskOnlyRow(task) || !getSelectedTask(index)
                                }
                                placeholder={costCodePlaceholder(task, index)}
                                onChange={(data) =>
                                  handleAcumaticaCostCodeChange(
                                    data,
                                    task,
                                    index
                                  )
                                }
                                aria-label={`Acumatica cost code for ${task?.label}`}
                                options={getCostCodes(index)}
                              />
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={4}
                            className="merge-mapping-table__empty"
                          >
                            No time entries to map. Any OpsEdge checklists on
                            this temporary project will still move to the
                            Acumatica project when you merge.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="merge-project-modal__step">
                <h6 className="merge-project-modal__step-title">
                  3. Checklists that will move ({checkListCount})
                </h6>
                {checkListCount > 0 ? (
                  <div className="merge-mapping-table-wrap">
                    <table
                      className="merge-mapping-table"
                      aria-label="Checklists that will move"
                    >
                      <thead>
                        <tr>
                          <th scope="col">Pole number</th>
                          <th scope="col">Work order</th>
                          <th scope="col">Status</th>
                          <th scope="col">Foreman</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sourceCheckLists.map((checkList) => (
                          <tr key={checkList?.id}>
                            <td>{checkList?.pole_number || "—"}</td>
                            <td>{checkList?.work_order_number || "—"}</td>
                            <td>
                              {CHECKLIST_STATUS[checkList?.status] ?? "—"}
                            </td>
                            <td>{checkList?.foreman_user_name || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="merge-project-modal__hint">
                    No checklists on this temporary project.
                  </p>
                )}
              </section>
            </div>

            <div className="modal-footer merge-project-modal__footer">
              <span
                id="merge-progress-status"
                className="merge-project-modal__progress"
                aria-live="polite"
              >
                {progressText}
              </span>
              <div className="merge-project-modal__actions">
                {loading && (
                  <div
                    className="spinner-border loader-styles"
                    role="status"
                    aria-label="Loading"
                  ></div>
                )}
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  data-bs-dismiss="modal"
                  onClick={() => resetFields()}
                >
                  Cancel
                </button>
                <button
                  data-bs-dismiss="modal"
                  type="button"
                  className="primary-btn-less-width"
                  aria-label="Merge projects"
                  aria-describedby="merge-progress-status"
                  disabled={!isReady}
                  title={
                    isReady
                      ? "Merge now"
                      : destinationChosen
                      ? "Map every row first"
                      : "Choose an Acumatica project first"
                  }
                  onClick={() => onClickMerge()}
                >
                  <img
                    className="merge-logo-styles"
                    src={MergeWhite}
                    alt=""
                  />
                  Merge
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MergeProjectModal;
